import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Package,
  Clock,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { useState } from "react";
import { ArrowDown, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { getLoads, Load } from "@/api/receiver/getLoads";

// Sample data for loads created over weeks
const loadsCreatedData = [
  { week: "Week 1 Jan", loads: 12, target: 15 },
  { week: "Week 2 Jan", loads: 18, target: 15 },
  { week: "Week 3 Jan", loads: 14, target: 15 },
  { week: "Week 4 Jan", loads: 16, target: 15 },
  { week: "Week 1 Feb", loads: 20, target: 15 },
  { week: "Week 2 Feb", loads: 13, target: 15 },
  { week: "Week 3 Feb", loads: 17, target: 15 },
  { week: "Week 4 Feb", loads: 19, target: 15 },
  { week: "Week 1 Mar", loads: 7, target: 15 },
  { week: "Week 2 Mar", loads: 10, target: 15 },
  { week: "Week 3 Mar", loads: 15, target: 15 },
  { week: "Week 4 Mar", loads: 20, target: 15 },
];

// Sample data for late drop-offs percentage by week
const lateDropoffData = [
  {
    week: "Week 1 Jan",
    latePercentage: 8.3,
    onTimePercentage: 91.7,
    totalLoads: 12,
  },
  {
    week: "Week 2 Jan",
    latePercentage: 5.6,
    onTimePercentage: 94.4,
    totalLoads: 18,
  },
  {
    week: "Week 3 Jan",
    latePercentage: 14.3,
    onTimePercentage: 85.7,
    totalLoads: 14,
  },
  {
    week: "Week 4 Jan",
    latePercentage: 6.3,
    onTimePercentage: 93.7,
    totalLoads: 16,
  },
  {
    week: "Week 1 Feb",
    latePercentage: 10.0,
    onTimePercentage: 90.0,
    totalLoads: 20,
  },
  {
    week: "Week 2 Feb",
    latePercentage: 15.4,
    onTimePercentage: 84.6,
    totalLoads: 13,
  },
  {
    week: "Week 3 Feb",
    latePercentage: 5.9,
    onTimePercentage: 94.1,
    totalLoads: 17,
  },
  {
    week: "Week 4 Feb",
    latePercentage: 10.5,
    onTimePercentage: 89.5,
    totalLoads: 19,
  },
  {
    week: "Week 1 Mar",
    latePercentage: 13.3,
    onTimePercentage: 86.7,
    totalLoads: 15,
  },
  {
    week: "Week 2 Mar",
    latePercentage: 4.5,
    onTimePercentage: 95.5,
    totalLoads: 22,
  },
  {
    week: "Week 3 Mar",
    latePercentage: 18.2,
    onTimePercentage: 81.8,
    totalLoads: 11,
  },
  {
    week: "Week 4 Mar",
    latePercentage: 11.1,
    onTimePercentage: 88.9,
    totalLoads: 18,
  },
];

// Current week summary data
const currentWeekSummary = {
  totalLoads: 18,
  onTimeDeliveries: 16,
  lateDeliveries: 2,
  averageDelay: 1.8, // hours
};

const COLORS = {
  onTime: "#10b981",
  late: "#ef4444",
  primary: "#3b82f6",
  secondary: "#8b5cf6",
};

type Props = {
  setWeeksToShow: (weeks: number) => void;
};

export default function ReceiverAnalytics({ setWeeksToShow }: Props) {
  const [timeRange, setTimeRange] = useState("4");
  const [selectedMetric, setSelectedMetric] = useState("loads");
  const [expandedWeeks, setExpandedWeeks] = useState<number[]>([]);

  const { data } = useQuery({
    queryKey: ["receiver", "loads"],
    queryFn: () => getLoads(),
  });

  const getFilteredData = () => {
    const weeksToShow = timeRange === "4" ? 4 : timeRange === "12" ? 12 : 8;
    return {
      loadsData: loadsCreatedData.slice(-weeksToShow),
      lateData: lateDropoffData.slice(-weeksToShow),
    };
  };

  const { loadsData, lateData } = getFilteredData();

  // Calculate trends (current week vs previous week)
  const currentWeekLoads = loadsData[loadsData.length - 1]?.loads || 0;
  const previousWeekLoads = loadsData[loadsData.length - 2]?.loads || 0;
  const loadsTrend =
    previousWeekLoads > 0
      ? ((currentWeekLoads - previousWeekLoads) / previousWeekLoads) * 100
      : 0;

  const currentWeekLate = lateData[lateData.length - 1]?.latePercentage || 0;
  const previousWeekLate = lateData[lateData.length - 2]?.latePercentage || 0;
  const lateTrend = currentWeekLate - previousWeekLate;

  // Group loads by weeks
  const weeklyLoads: Load[][] = [];
  if (data) {
    const loadsByWeek: { [key: string]: Load[] } = {};

    data.forEach((load: Load) => {
      const date = load.deliveryStart;
      if (!date) return;

      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay() + 1);
      const weekKey = weekStart.toISOString().split("T")[0];

      if (!loadsByWeek[weekKey]) loadsByWeek[weekKey] = [];
      loadsByWeek[weekKey].push(load);
    });

    weeklyLoads.push(
      ...Object.entries(loadsByWeek)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([_, loads]) => loads)
    );
  }

  const weeksToDisplay = weeklyLoads.slice(0, parseInt(timeRange));

  return (
    <div className="min-h-[50%] py-">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Track your freight performance and delivery metrics
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Select
              value={timeRange}
              onValueChange={(val) => {
                setTimeRange(val);
                setWeeksToShow(parseInt(val));
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4">Last 4 weeks</SelectItem>
                <SelectItem value="8">Last 8 weeks</SelectItem>
                <SelectItem value="12">Last 12 weeks</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Loads (This Week)
              </CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentWeekSummary.totalLoads}
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                {loadsTrend > 0 ? (
                  <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                )}
                <span
                  className={loadsTrend > 0 ? "text-green-500" : "text-red-500"}
                >
                  {Math.abs(loadsTrend).toFixed(1)}%
                </span>
                <span className="ml-1">from last week</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                On-Time Deliveries
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentWeekSummary.onTimeDeliveries}
              </div>
              <p className="text-xs text-muted-foreground">
                {(
                  (currentWeekSummary.onTimeDeliveries /
                    currentWeekSummary.totalLoads) *
                  100
                ).toFixed(1)}
                % success rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Late Deliveries
              </CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {currentWeekSummary.lateDeliveries}
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                {lateTrend < 0 ? (
                  <TrendingDown className="h-3 w-3 text-green-500 mr-1" />
                ) : (
                  <TrendingUp className="h-3 w-3 text-red-500 mr-1" />
                )}
                <span
                  className={lateTrend < 0 ? "text-green-500" : "text-red-500"}
                >
                  {Math.abs(lateTrend).toFixed(1)}%
                </span>
                <span className="ml-1">from last week</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Avg Transit Time
              </CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentWeekSummary.averageDelay}h
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Loads Created Chart */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>Loads Shipped Over Time</CardTitle>
                <CardDescription>
                  Weekly load creation vs target
                </CardDescription>
              </div>

              <Button variant={"outline"} onClick={() => alert("Downloaded")}>
                <ArrowDown />
              </Button>
            </CardHeader>

            <CardContent>
              <ChartContainer
                config={{
                  loads: {
                    label: "Loads Created",
                    color: COLORS.primary,
                  },
                  target: {
                    label: "Target",
                    color: COLORS.secondary,
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                  className={"lg:pr-4"}
                >
                  <BarChart data={loadsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="loads"
                      fill="var(--color-loads)"
                      name="Loads Created"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Late Drop-offs Percentage Chart */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="">
                <CardTitle>Late Drop-off Percentage</CardTitle>
                <CardDescription>
                  Percentage of late deliveries by week
                </CardDescription>
              </div>

              <Button variant={"outline"} onClick={() => alert("Downloaded")}>
                <ArrowDown />
              </Button>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={{
                  latePercentage: {
                    label: "Late %",
                    color: COLORS.late,
                  },
                }}
                className="h-[300px]"
              >
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                  className={`lg:pr-4`}
                >
                  <LineChart data={lateData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="week"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      domain={[0, 20]}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <ChartTooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: any) => [
                        `${value}%`,
                        "Late Deliveries",
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="latePercentage"
                      stroke="var(--color-latePercentage)"
                      strokeWidth={3}
                      dot={{
                        fill: "var(--color-latePercentage)",
                        strokeWidth: 2,
                        r: 4,
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Loads Data Table */}
        {/* Weekly Performance Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle>Weekly Performance Breakdown</CardTitle>
              <CardDescription>
                Detailed view of weekly loads and delivery performance
              </CardDescription>
            </div>
            <Button variant={"outline"} onClick={() => alert("Downloaded")}>
              <ArrowDown />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Week</th>
                    <th className="text-right p-2">Total Loads</th>
                    <th className="text-right p-2">On Time</th>
                    <th className="text-right p-2">Late</th>
                    <th className="text-right p-2">Late %</th>
                  </tr>
                </thead>
                <tbody>
                  {loadsData.map((item, index) => {
                    const lateInfo = lateData[index];
                    const lateCount = Math.round(
                      (lateInfo.latePercentage / 100) * item.loads
                    );
                    const onTimeCount = item.loads - lateCount;

                    return (
                      <tr key={item.week} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{item.week}</td>
                        <td className="text-right p-2">{item.loads}</td>

                        <td className="text-right p-2 text-green-600">
                          {onTimeCount}
                        </td>
                        <td className="text-right p-2 text-red-600">
                          {lateCount}
                        </td>
                        <td className="text-right p-2">
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              lateInfo.latePercentage < 10
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {lateInfo.latePercentage.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
